"use client";

import { CmsContainedImage } from "@/components/cms/CmsContainedImage";
import { resolveApiAssetUrl } from "@/lib/backend";
import { cn } from "@/lib/utils";

export type ImageSectionData = {
  image_url?: string;
  image_alt?: string;
  caption?: string;
  align?: "left" | "center" | "right" | "full-bleed";
};

export function ImageSection({ data }: { data: ImageSectionData }) {
  const src = resolveApiAssetUrl(data.image_url);
  if (!src) return null;

  const align = data.align ?? "center";
  const alt = data.image_alt?.trim() || data.caption?.trim() || "";
  const caption = data.caption?.trim();

  if (align === "full-bleed") {
    return (
      <section className="cms-section-contained py-[var(--section-space)]">
        <figure className="mx-auto w-full max-w-full min-w-0 px-4 md:px-6">
          <CmsContainedImage
            src={data.image_url}
            alt={alt}
            frameClassName="cms-standalone-image w-full"
          />
          {caption ? (
            <figcaption className="mx-auto mt-4 max-w-3xl text-center text-sm text-slate-500 md:text-base">
              {caption}
            </figcaption>
          ) : null}
        </figure>
      </section>
    );
  }

  const figureClass = cn(
    "flex w-full max-w-full min-w-0 flex-col gap-3",
    align === "left" && "items-start text-left",
    align === "center" && "items-center text-center",
    align === "right" && "items-end text-right",
  );

  return (
    <section className="cms-section-contained py-[var(--section-space)]">
      <div className="mx-auto w-full max-w-5xl min-w-0 px-4 sm:px-5 md:px-6">
        <figure className={figureClass}>
          <CmsContainedImage
            src={data.image_url}
            alt={alt}
            frameClassName="cms-standalone-image rounded-2xl border border-slate-200/80 bg-slate-100 shadow-lg shadow-slate-900/5 ring-1 ring-slate-900/5"
          />
          {caption ? (
            <figcaption className="mt-2 max-w-3xl text-pretty text-sm leading-relaxed text-slate-500 md:text-base">
              {caption}
            </figcaption>
          ) : null}
        </figure>
      </div>
    </section>
  );
}
